"use client";

/**
 * AlertFlowingEdge — thin icy line with a subtle flowing highlight pulse AND
 * a one-shot draw-in animation when the edge first appears.
 *
 *  - Base path: ~1.5px stroke at `ICY.edgeStroke` opacity, always visible.
 *  - On first mount: the base path animates `stroke-dashoffset` from 100%
 *    down to 0 so the line "draws itself" from source → target.
 *  - After the draw-in finishes, a secondary highlight path loops a gentle
 *    pearl pulse along the stroke so the canvas feels alive.
 *  - Respects `prefers-reduced-motion` — static line only.
 */

import { memo, useEffect, useRef, useState } from "react";
import { getSmoothStepPath, type EdgeProps } from "@xyflow/react";
import { ICY, PALETTE } from "@/lib/alert-theme";

function AlertFlowingEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: EdgeProps) {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 18,
  });

  const idx = parseInt(id.replace(/\D/g, ""), 10) || 0;
  const delay = `${(idx * 0.3).toFixed(2)}s`;
  const gradId = `alert-edge-hl-${idx}`;
  const arrowId = `alert-edge-arrow-${idx}`;

  // Measure the path length once so we can start fully "hidden" (dashoffset =
  // length) and animate to 0 for the draw-in.
  const basePathRef = useRef<SVGPathElement>(null);
  const [pathLen, setPathLen] = useState(0);
  useEffect(() => {
    if (basePathRef.current) {
      try {
        const l = basePathRef.current.getTotalLength();
        if (Number.isFinite(l) && l > 0) setPathLen(l);
      } catch {
        /* ignore — SSR path / edge case */
      }
    }
  }, [edgePath]);

  return (
    <>
      <defs>
        {/* Moving highlight — pearl peak sandwiched between transparent ends.
            Animated via stroke-dashoffset on the highlight path below, so the
            gradient itself stays static and only the stroke "window" moves. */}
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PALETTE.icy[200]} stopOpacity="0" />
          <stop offset="45%" stopColor={PALETTE.pearl} stopOpacity="0.95" />
          <stop offset="55%" stopColor={PALETTE.pearl} stopOpacity="0.95" />
          <stop offset="100%" stopColor={PALETTE.icy[200]} stopOpacity="0" />
        </linearGradient>

        {/* Flow arrow at the target end — a small icy triangle that
            inherits the edge stroke color. `orient="auto"` rotates the
            marker to match the path's direction so it always points
            toward the next node, no matter how the smooth-step path
            bends. `markerUnits="userSpaceOnUse"` keeps the tip size
            stable across zooms. */}
        <marker
          id={arrowId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="9"
          markerHeight="9"
          markerUnits="userSpaceOnUse"
          orient="auto-start-reverse"
        >
          <path
            d="M0,0 L10,5 L0,10 L2.5,5 Z"
            fill={ICY.edgeStroke}
            opacity="0.95"
          />
        </marker>
      </defs>

      {/* Base icy line — draw-in on mount, then static. The marker is
          attached here so the arrow head draws with the stroke. */}
      <path
        ref={basePathRef}
        d={edgePath}
        fill="none"
        stroke={ICY.edgeStroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        markerEnd={`url(#${arrowId})`}
        // If we have a measured length, run the draw-in; otherwise render
        // statically (first paint without measurement yet).
        {...(pathLen
          ? {
              style: {
                strokeDasharray: pathLen,
                strokeDashoffset: pathLen,
                animation: `alertEdgeDrawIn 0.7s cubic-bezier(0.22,1,0.36,1) forwards`,
                animationDelay: delay,
              },
            }
          : {})}
      />

      {/* Highlight pulse — same geometry, short visible window that slides
          along the stroke. Starts AFTER the draw-in via animationDelay. */}
      <path
        className="alert-edge-highlight"
        d={edgePath}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeDasharray="20 160"
        strokeDashoffset={0}
        style={{ animationDelay: `calc(${delay} + 0.8s)` }}
      />

      <style jsx>{`
        @keyframes alertEdgeDrawIn {
          to {
            stroke-dashoffset: 0;
          }
        }
        .alert-edge-highlight {
          animation: alertEdgeFlow 3.6s linear infinite;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        @keyframes alertEdgeFlow {
          0% {
            stroke-dashoffset: 180;
            opacity: 0;
          }
          8% {
            opacity: 1;
          }
          92% {
            opacity: 1;
          }
          100% {
            stroke-dashoffset: 0;
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .alert-edge-highlight {
            animation: none;
            opacity: 0;
          }
          :global(path) {
            stroke-dasharray: none !important;
            stroke-dashoffset: 0 !important;
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
}

export const AlertFlowingEdge = memo(AlertFlowingEdgeComponent);
